import React, { useState } from 'react';
import { Loader, CircleCheck as CheckCircle, User, Home, Briefcase, Users, FileText, Calendar, MapPin, FilePen } from 'lucide-react';
import type { ManualRegistrationData } from './types';
import { formatCurrency, calculateLoanDetails, calculateAutonomousLoanDetails } from './types';
import AiReviewPanel, { MIN_JUSTIFICATION } from './AiReviewPanel';
import type { AiReviewState } from './aiReview';

interface Props {
  data: ManualRegistrationData;
  contractTempId: string;
  isSubmitting: boolean;
  error: string | null;
  aiReview: AiReviewState | null;
  onAiReviewChange: (state: AiReviewState) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  onEditDocuments: () => void;
  onConfirm: (declared: boolean) => void;
}

export default function ReviewStep({
  data, contractTempId, isSubmitting, error, aiReview, onAiReviewChange, justification, onJustificationChange, onEditDocuments, onConfirm,
}: Props) {
  const [declared, setDeclared] = useState(false);
  const isClt = data.clientType === 'clt';
  const aiRunning = isClt && (!aiReview || aiReview.phase === 'running');
  const needsJustification = isClt && aiReview?.phase === 'done' && aiReview.overall === 'problem'
    && justification.trim().length < MIN_JUSTIFICATION;
  const fileCount = data.documents.reduce((sum, d) => sum + d.urls.length, 0);

  const details = data.clientType === 'autonomous'
    ? calculateAutonomousLoanDetails(data.loan.amount, data.loan.installments, data.loan.interestRate)
    : calculateLoanDetails(data.loan.amount, data.loan.installments, data.loan.interestRate);

  const formatAddr = (a: typeof data.enderecoResidencial) =>
    [a.logradouro, a.numero, a.complemento, a.bairro, a.cidade, a.estado]
      .filter(Boolean)
      .join(', ');

  const SectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
      {icon}
      <span className="text-sm font-semibold text-gray-700">{title}</span>
    </div>
  );

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between items-start gap-4 py-1.5">
      <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Revisão e Confirmação</h2>
        <p className="text-gray-600 text-sm">Confira todos os dados antes de criar o contrato.</p>
      </div>

      {/* Empréstimo */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <SectionTitle icon={<Calendar className="w-4 h-4 text-gray-400" />} title="Empréstimo" />
        <div className="p-4 space-y-1">
          <Row label="Valor solicitado" value={formatCurrency(parseFloat(data.loan.amount) || 0)} />
          <Row label="Parcelas" value={`${data.loan.installments}x ${formatCurrency(details.installmentAmount)}`} />
          <Row label="Taxa de juros" value={`${data.loan.interestRate}%`} />
          <Row label="Total a pagar" value={<span className="font-bold text-gray-900">{formatCurrency(details.totalAmount)}</span>} />
          <Row label="Ruta" value={`Ruta ${data.loan.ruta}`} />
        </div>
        {data.loan.installmentDates.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Vencimentos</p>
            <div className="flex flex-wrap gap-2">
              {data.loan.installmentDates.map((d, i) => (
                <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg">
                  {i + 1}. {d.toLocaleDateString('pt-BR')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dados pessoais */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <SectionTitle icon={<User className="w-4 h-4 text-gray-400" />} title="Dados do Cliente" />
        <div className="p-4 space-y-1">
          <Row label="Nome" value={data.personal.name} />
          <Row label="CPF" value={data.personal.cpf} />
          <Row label="Telefone" value={data.personal.phone} />
          <Row label="Chave PIX" value={data.personal.pixKey} />
          {data.personal.bank && <Row label="Banco" value={data.personal.bank} />}
          {(data.personal.serviceEntryTime || data.personal.serviceExitTime) && (
            <Row
              label="Horário serviço"
              value={`${data.personal.serviceEntryTime || '--'} – ${data.personal.serviceExitTime || '--'}`}
            />
          )}
        </div>
      </div>

      {/* Endereços */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <SectionTitle icon={<Home className="w-4 h-4 text-gray-400" />} title="Endereços" />
        <div className="p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Residencial</p>
            <p className="text-sm text-gray-800">{formatAddr(data.enderecoResidencial) || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Comercial</p>
            <p className="text-sm text-gray-800">{formatAddr(data.enderecoComercial) || '—'}</p>
          </div>
        </div>
      </div>

      {/* Contatos */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <SectionTitle icon={<Users className="w-4 h-4 text-gray-400" />} title={`Contatos (${data.contacts.length})`} />
        <div className="p-4 space-y-2">
          {data.contacts.map((c, i) => (
            <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{c.nome}</p>
                <p className="text-xs text-gray-500">{c.telefone} · {c.relacao}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {c.photoUrl && (
                  <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                  </span>
                )}
                {c.conversationUrl && (
                  <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Documentos */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <SectionTitle icon={<FileText className="w-4 h-4 text-gray-400" />} title="Documentos" />
        <div className="p-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{fileCount}</span> arquivo{fileCount !== 1 ? 's' : ''} anexado{fileCount !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            onClick={onEditDocuments}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-40"
          >
            <FilePen className="w-3.5 h-3.5" /> Corrigir documentos
          </button>
        </div>
      </div>

      {isClt && (
        <AiReviewPanel
          data={data}
          contractTempId={contractTempId}
          review={aiReview}
          onReviewChange={onAiReviewChange}
          justification={justification}
          onJustificationChange={onJustificationChange}
          onEditDocuments={onEditDocuments}
        />
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Declaração */}
      <label className="flex items-start gap-3 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
        <input
          type="checkbox"
          checked={declared}
          onChange={(e) => setDeclared(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
        />
        <span className="text-sm text-gray-700 leading-snug">
          Declaro que todas as informações foram verificadas e estão corretas, e autorizo a criação deste contrato.
        </span>
      </label>

      <button
        onClick={() => onConfirm(declared)}
        disabled={!declared || isSubmitting || aiRunning || needsJustification}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <><Loader className="w-5 h-5 animate-spin" /> Criando contrato...</>
        ) : aiRunning ? (
          <><Loader className="w-5 h-5 animate-spin" /> Aguardando revisão automática...</>
        ) : (
          'Criar Contrato'
        )}
      </button>
    </div>
  );
}
